import React, { useEffect, useState } from 'react';
import { Bot, Clock, Terminal, CheckCircle2, Timer } from 'lucide-react';
import { cronsList } from '../mock/mock';
import MiniSparkline from './MiniSparkline';

const enrichCron = (c, idx) => {
  const colors = ['#22d3ee', '#a855f7', '#ec4899', '#f59e0b', '#34d399', '#38bdf8'];
  return {
    ...c,
    color: colors[idx % colors.length],
    secondsUntilNext: 30 + Math.floor(Math.random() * 3000),
    running: idx < 3,
    step: '',
    progress: idx < 3 ? Math.floor(Math.random() * 90) + 5 : 0,
    tasks: 3 + Math.floor(Math.random() * 5),
    done: idx < 3 ? Math.floor(Math.random() * 3) : 0,
  };
};

const CRON_STEPS = {
  0: ['scanning sitemaps EU', 'filtering liquidations', 'ranking by margin', 'writing prospects.json', 'notifying discord'],
  1: ['launching 247 workers', 'proxy rotation · pool A', 'fusion CSV · batch #12', 'deduplicate EAN', 'compressing archive'],
  2: ['aggregating night runs', 'building charts', 'gpt summary v4', 'posting to #debrief', 'attach CSVs'],
};

const genCronLog = (name, step) => {
  const short = name.replace('KAVENDOR — ', '').slice(0, 22);
  const details = [
    `chunk ${Math.floor(Math.random() * 500)} → OK`,
    `${Math.floor(Math.random() * 40 + 5)} items pushed`,
    `${Math.floor(Math.random() * 900 + 100)}ms latency`,
    `agent hermes step ${Math.floor(Math.random() * 10)}`,
    `retry attempt ${Math.floor(Math.random() * 3)}`,
  ];
  return `${new Date().toISOString().slice(11, 19)} · ${short.padEnd(24, ' ')} [${step}] ${details[Math.floor(Math.random() * details.length)]}`;
};

const fmtCountdown = (s) => {
  if (s <= 0) return 'running…';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
};

const LiveCrons = () => {
  const [crons, setCrons] = useState(() => cronsList.slice(0, 6).map(enrichCron));
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    const int = setInterval(() => {
      setCrons((prev) => prev.map((c, idx) => {
        if (!c.running) return { ...c, secondsUntilNext: Math.max(0, c.secondsUntilNext - 1) };
        const nextProgress = c.progress + Math.random() * 3;
        const steps = CRON_STEPS[idx] || CRON_STEPS[0];
        const stepIdx = Math.floor((nextProgress / 100) * steps.length);
        return {
          ...c,
          progress: nextProgress >= 100 ? 0 : nextProgress,
          done: nextProgress >= 100 ? c.done + 1 : c.done,
          step: steps[Math.min(stepIdx, steps.length - 1)],
        };
      }));
    }, 1000);
    return () => clearInterval(int);
  }, []);

  useEffect(() => {
    const int = setInterval(() => {
      setCrons((current) => {
        const running = current.filter((c) => c.running);
        if (!running.length) return current;
        const c = running[Math.floor(Math.random() * running.length)];
        setLogs((l) => [genCronLog(c.name, c.step || 'idle'), ...l].slice(0, 30));
        return current;
      });
    }, 900);
    return () => clearInterval(int);
  }, []);

  const runningCount = crons.filter((c) => c.running).length;

  return (
    <section className="rounded-2xl border border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-amber-400" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-mono">Crons Kavendor</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1.5">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
            </span>
            <span className="text-[10px] font-mono text-amber-300">{runningCount} EXÉCUTION</span>
          </span>
          <span className="text-[10px] font-mono text-white/40">{cronsList.length} total</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {crons.map((c, idx) => (
          <CronCard key={idx} c={c} delay={idx * 0.05} />
        ))}
      </div>

      <div className="flex-1 rounded-lg border border-amber-400/15 bg-black/60 overflow-hidden flex flex-col min-h-[140px]">
        <div className="px-3 py-1.5 flex items-center gap-2 border-b border-white/5 bg-white/[0.02]">
          <Terminal className="h-3 w-3 text-amber-400" />
          <span className="text-[9px] font-mono text-white/50 uppercase tracking-widest">cron.scheduler</span>
          <span className="ml-auto text-[9px] font-mono text-white/30">{logs.length} events</span>
        </div>
        <div className="flex-1 overflow-hidden font-mono text-[10px] leading-relaxed px-3 py-2">
          <div className="space-y-0.5">
            {logs.slice(0, 8).map((line, i) => (
              <div
                key={i + line}
                className="text-amber-200/80 whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ opacity: 1 - i * 0.11, animation: i === 0 ? 'log-in 0.4s ease' : undefined }}
              >
                <span className="text-white/25 mr-1">›</span>{line}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const CronCard = ({ c, delay }) => {
  const shortName = c.name.replace('KAVENDOR — ', '').replace('KAVENDOR—', '');
  return (
    <div
      className="relative rounded-lg border bg-black/40 p-3 overflow-hidden"
      style={{
        borderColor: c.running ? `${c.color}55` : 'rgba(255,255,255,0.06)',
        boxShadow: c.running ? `0 0 20px ${c.color}22` : 'none',
        animation: `card-in 0.5s ease ${delay}s both`,
      }}
    >
      {c.running && (
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${c.color}, transparent)`, animation: 'stream-x 2s linear infinite' }} />
        </div>
      )}

      <div className="flex items-start justify-between mb-2 gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {c.running ? (
            <span className="h-1.5 w-1.5 rounded-full animate-pulse flex-shrink-0" style={{ background: c.color, boxShadow: `0 0 6px ${c.color}` }} />
          ) : (
            <CheckCircle2 className="h-3 w-3 text-emerald-400 flex-shrink-0" />
          )}
          <span className="font-mono text-[11px] text-white truncate" title={shortName}>{shortName}</span>
        </div>
        <span className="text-[9px] font-mono text-white/40 whitespace-nowrap flex items-center gap-1">
          <Clock className="h-2.5 w-2.5" /> {c.schedule}
        </span>
      </div>

      {c.running ? (
        <>
          <div className="text-[10px] font-mono text-white/60 mb-1.5 truncate italic">
            <span className="text-white/40">▸</span> {c.step || 'starting…'}
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mb-2 relative">
            <div
              className="h-full rounded-full transition-all duration-700 ease-out"
              style={{
                width: `${c.progress}%`,
                background: `linear-gradient(90deg, ${c.color}88, ${c.color})`,
                boxShadow: `0 0 8px ${c.color}`,
              }}
            />
            <div className="absolute inset-y-0 w-8 pointer-events-none" style={{ background: `linear-gradient(90deg, transparent, ${c.color}88, transparent)`, animation: 'sweep 1.5s ease-in-out infinite' }} />
          </div>
          <MiniSparkline color={c.color} />
          <div className="grid grid-cols-3 gap-1 mt-2">
            <MiniMetric label="Runs" value={c.done} />
            <MiniMetric label="Tasks" value={c.tasks} />
            <MiniMetric label="%" value={Math.round(c.progress)} />
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center gap-1.5 text-[10px] font-mono text-white/50 mb-2">
            <Timer className="h-2.5 w-2.5 text-cyan-400" />
            <span>Prochain</span>
            <span className="ml-auto text-cyan-300 tabular-nums">{fmtCountdown(c.secondsUntilNext)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mb-2 relative">
            <div
              className="h-full rounded-full"
              style={{
                width: `${100 - Math.min(100, (c.secondsUntilNext / 3600) * 100)}%`,
                background: `linear-gradient(90deg, rgba(52,211,153,0.5), rgba(52,211,153,0.9))`,
                transition: 'width 1s linear',
              }}
            />
          </div>
          <div className="grid grid-cols-2 gap-1 mt-1">
            <MiniMetric label="Dernier" value={c.last?.slice(11, 16) || '—'} />
            <MiniMetric label="Statut" value="OK" />
          </div>
        </>
      )}
    </div>
  );
};

const MiniMetric = ({ label, value }) => (
  <div className="text-center">
    <div className="font-mono text-[11px] text-white/90 tabular-nums leading-none">{value}</div>
    <div className="font-mono text-[8px] text-white/35 uppercase mt-0.5">{label}</div>
  </div>
);

export default LiveCrons;
